import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ExternalLink, Loader2 } from 'lucide-react';
import { useAdminCylinderAudits, type AdminAuditRow, type AuditStatus } from '@/hooks/admin/useCylinderAudits';

type Filter = AuditStatus | 'all';

const STATUS_LABEL: Record<AuditStatus, { text: string; cls: string }> = {
  balanced: { text: 'Counts matched', cls: 'bg-emerald-100 text-emerald-800' },
  adjusted: { text: 'Adjusted', cls: 'bg-blue-100 text-blue-800' },
  pending_approval: { text: 'Needs approval', cls: 'bg-amber-100 text-amber-800' },
  invoice_failed: { text: 'QuickBooks failed', cls: 'bg-red-100 text-red-800' },
  declined: { text: 'Declined', cls: 'bg-slate-200 text-slate-700' },
};

export function auditStatusBadge(status: string) {
  const s = STATUS_LABEL[status as AuditStatus] ?? { text: status, cls: 'bg-slate-100 text-slate-700' };
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.cls}`}>{s.text}</span>;
}

export function auditDeltas(a: { counts: Array<{ label: string; delta: number }> }) {
  const moved = a.counts.filter((c) => c.delta !== 0);
  if (moved.length === 0) return <span className="text-brix-muted">no change</span>;
  return (
    <>
      {moved.map((c) => (
        <span key={c.label} className={`mr-2 ${c.delta > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
          {c.label} {c.delta > 0 ? '+' : ''}{c.delta}
        </span>
      ))}
    </>
  );
}

/**
 * Every Tank Rental Audit the drivers have filed, across all accounts —
 * the rentals section's own view of the same records /admin/audits reviews.
 * Each row links to the account's rental page (its history is there) and to
 * the audit record with its photos and signature.
 */
export default function RentalsAudits() {
  const [filter, setFilter] = useState<Filter>('all');
  const { data, isLoading, error } = useAdminCylinderAudits(filter);
  const rows = useMemo(() => data ?? [], [data]);

  return (
    <div>
      <h1 className="font-display text-xl font-bold tracking-display-tight text-brix-ink">Audits & adjustments</h1>
      <p className="mt-1 text-sm text-brix-muted">
        Every on-site tank count a driver has filed. A count that differs from the books becomes a $0 CR-ADJ adjustment invoice; a shortage waits for a manager’s approval first.
        Open a customer to see how their count got where it is, or to start an audit there.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {(['all', 'pending_approval', 'adjusted', 'balanced', 'invoice_failed', 'declined'] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === f ? 'border-brix-primary bg-brix-primary text-white' : 'border-brix-border bg-brix-surface text-brix-ink'}`}
          >
            {f === 'all' ? 'Everything' : STATUS_LABEL[f].text}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-brix-border bg-brix-surface shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-brix-muted"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Loading…</span></div>
        ) : error ? (
          <div className="p-4 text-sm text-brix-warning">Couldn’t load the audits: {error.message}</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brix-muted">No audits{filter === 'all' ? ' yet' : ' with that status'}.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-brix-surface-alt text-left text-[11px] uppercase tracking-wide text-brix-muted">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Audit</th>
                <th className="hidden px-3 py-2 md:table-cell">Counted by</th>
                <th className="px-3 py-2">Result</th>
                <th className="px-3 py-2 text-right">Record</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a: AdminAuditRow) => (
                <tr key={a.id} className="border-t border-brix-border align-top">
                  <td className="px-3 py-2 text-xs text-brix-muted whitespace-nowrap">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2">
                    {a.qbo_customer_id ? (
                      <Link to={`/audit/rentals/${a.qbo_customer_id}`} className="font-medium text-brix-ink hover:underline">{a.customer_name ?? `QuickBooks #${a.qbo_customer_id}`}</Link>
                    ) : (
                      <span className="font-medium text-brix-ink">{a.customer_name ?? 'unknown'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">{a.doc_number}</div>
                    <div className="mt-0.5">{auditStatusBadge(a.status)}</div>
                    {a.status === 'invoice_failed' && a.invoice_error ? (
                      <div className="mt-1 max-w-xs text-xs text-red-800">{a.invoice_error}</div>
                    ) : null}
                  </td>
                  <td className="hidden px-3 py-2 text-xs md:table-cell">
                    {a.signed_name}
                    {a.driver_name && a.driver_name !== a.signed_name ? <div className="text-brix-muted">driver {a.driver_name}</div> : null}
                    {a.manager_name ? <div className="text-brix-muted">manager {a.manager_name}</div> : null}
                  </td>
                  <td className="px-3 py-2 text-xs">{auditDeltas(a)}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    <Link to={`/admin/audits/${a.id}`} className="inline-flex items-center gap-1 text-brix-primary hover:underline">
                      <Camera className="h-3.5 w-3.5" /> {a.photo_count} photo{a.photo_count === 1 ? '' : 's'} · open
                    </Link>
                    {a.qbo_invoice_id ? (
                      <a href={`https://app.qbo.intuit.com/app/invoice?txnId=${a.qbo_invoice_id}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-brix-muted hover:underline">
                        <ExternalLink className="h-3 w-3" /> in QuickBooks
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
